import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Alert,
  IconButton,
  Divider,
  List,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useLanguageNavigation } from '../hooks/useLanguageNavigation';
import api from '../services/api';
import { formatBalance, formatCurrency } from '../utils/format';

const ExitStrategyTab = ({ coinSymbol, currentPrice, balance }) => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const { navigateWithLang } = useLanguageNavigation();
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [strategyToDelete, setStrategyToDelete] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    fetchStrategies();
  }, [coinSymbol]);

  const fetchStrategies = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/api/exit-strategies/coin/${coinSymbol}`);
      setStrategies(response.data.strategies || []);
    } catch (err) {
      console.error('Error fetching strategies:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteStrategy = async (strategyId) => {
    setStrategyToDelete(strategyId);
    setConfirmOpen(true);
  };

  const confirmDelete = async () => {
    if (!strategyToDelete) return;
    try {
      await api.delete(`/api/exit-strategies/${strategyToDelete}`);
      setStrategyToDelete(null);
      setConfirmOpen(false);
      fetchStrategies();
    } catch (err) {
      setError(err.message);
      setStrategyToDelete(null);
      setConfirmOpen(false);
    }
  };

  const cancelDelete = () => {
    setStrategyToDelete(null);
    setConfirmOpen(false);
  };

  const handleManageStrategies = () => {
            navigateWithLang(`exit-strategy/${coinSymbol}`);
  };



  const getStrategyTypeLabel = (type) => {
    return type === 'take_profit' ? 'Take Profit' : 'Stop Loss';
  };

  if (loading) {
    return <Typography>Loading...</Typography>;
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Manage Strategies Button - Top */}
      <Button 
        variant="contained" 
        onClick={handleManageStrategies}
        fullWidth
        size="large"
        sx={{ mb: 3 }}
      >
        {t('exitStrategy.manage')}
      </Button>

      {/* Active Strategies */}
      <Box sx={{ mb: 3 }}>
        {strategies.length === 0 ? (
          <Typography color="textSecondary">No exit strategies configured</Typography>
        ) : (
          <List>
            {strategies.map((strategy) => {
              let steps = [];
              if (typeof strategy.steps === 'string') {
                steps = JSON.parse(strategy.steps || '[]');
              } else if (Array.isArray(strategy.steps)) {
                steps = strategy.steps;
              } else {
                steps = [];
              }
              return (
                <Card key={strategy.id} sx={{ mb: 2 }}>
                  <CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Box>
                        <Typography variant="h6">
                          {getStrategyTypeLabel(strategy.strategy_type)}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Target: {formatCurrency(strategy.target_price)}
                        </Typography>
                      </Box>
                      <Box>
                        <IconButton onClick={() => handleDeleteStrategy(strategy.id)} size="small" color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    </Box>
                    <Divider sx={{ my: 1 }} />
                    {steps.map((step, index) => {
                      // Debug log for step and isTriggered
                      // console.log('[DEBUG ExitStrategyTab step]', step, 'notified_at:', step.notified_at);
                      const isTriggered = step.notified_at || 
                        (strategy.strategy_type === 'take_profit' && currentPrice >= step.trigger_price) ||
                        (strategy.strategy_type === 'stop_loss' && currentPrice <= step.trigger_price);
                      // console.log(`[DEBUG ExitStrategyTab isTriggered] Step ${step.step_index}:`, isTriggered);
                      return (
                        <Box key={index} sx={{ mb: 2, p: 1, border: '1px solid #e0e0e0', borderRadius: 1, bgcolor: '#fafafa' }}>
                          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, alignItems: 'center' }}>
                            <Typography variant="subtitle2" fontWeight="bold">
                              {step.step_index === 1 ? '1st' : step.step_index === 2 ? '2nd' : step.step_index === 3 ? '3rd' : `${step.step_index}th`} Exit
                            </Typography>
                            <Typography variant="subtitle2" fontWeight="bold" sx={{ justifySelf: 'center' }}>
                              {formatCurrency(step.trigger_price)}
                            </Typography>
                            {currentPrice > 0 ? (
                              <Typography 
                                variant="subtitle2" 
                                fontWeight="bold"
                                sx={{ 
                                  color: step.trigger_price > currentPrice ? '#4caf50' : step.trigger_price < currentPrice ? '#f44336' : 'inherit',
                                  justifySelf: 'end'
                                }}
                              >
                                {step.trigger_price > currentPrice ? '+' : ''}{(((step.trigger_price - currentPrice) / currentPrice) * 100).toFixed(1)}%
                              </Typography>
                            ) : (
                              <Box /> // Empty box to maintain grid structure
                            )}
                          </Box>
                          
                          {balance && step.percentage_to_sell && step.trigger_price && (
                            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 2, alignItems: 'center' }}>
                              <Typography variant="body2" color="textSecondary">
                                {step.percentage_to_sell}% of holdings
                              </Typography>
                              <Typography variant="body2" color="textSecondary" sx={{ justifySelf: 'center' }}>
                                {formatBalance((balance * Number(step.percentage_to_sell) / 100))} {coinSymbol}
                              </Typography>
                              <Typography variant="body2" fontWeight="bold" sx={{ justifySelf: 'end' }}>
                                {formatCurrency((balance * Number(step.percentage_to_sell) / 100) * Number(step.trigger_price))}
                              </Typography>
                            </Box>
                          )}
                          
                          {isTriggered && (
                            <Box sx={{ mt: 1, textAlign: 'center' }}>
                              <Chip 
                                label={step.notified_at ? "Triggered" : "Active"} 
                                size="small" 
                                color={step.notified_at ? "warning" : "success"} 
                              />
                            </Box>
                          )}
                        </Box>
                      );
                    })}
                  </CardContent>
                </Card>
              );
            })}
          </List>
        )}
      </Box>

      {/* Manage Strategies Button */}
      <Button 
        variant="contained" 
        onClick={handleManageStrategies}
        fullWidth
        size="large"
      >
        {t('exitStrategy.manage')}
      </Button>

      {/* Confirm Delete Dialog */}
      <Dialog open={confirmOpen} onClose={cancelDelete}>
        <DialogTitle>Delete Exit Strategy?</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this exit strategy?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete} color="primary">Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ExitStrategyTab; 